import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Section } from '../ui/Section';
import { Card } from '../ui/Card';
import { GeometricPattern } from '../../utils/patterns';
import { typography } from '../../utils/theme';
import { SparklesIcon, CodeIcon, PaletteIcon, RocketIcon, HeartIcon, ZapIcon, BrainIcon, TargetIcon } from 'lucide-react';
export function AboutApp() {
  const {
    getThemeClasses,
    t
  } = useTheme();
  const themeClasses = getThemeClasses();
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  const coreValues = [{
    icon: SparklesIcon,
    title: 'Creativiteit',
    description: 'Innovatieve oplossingen door design en technologie',
    color: 'from-purple-500 to-violet-500',
    rotation: -2
  }, {
    icon: TargetIcon,
    title: 'Gebruikersgericht',
    description: 'Altijd vanuit de gebruiker gedacht',
    color: 'from-blue-500 to-cyan-500',
    rotation: 3
  }, {
    icon: ZapIcon,
    title: 'Efficiëntie',
    description: 'Slimme systemen die tijd besparen',
    color: 'from-amber-500 to-orange-500',
    rotation: -1
  }, {
    icon: HeartIcon,
    title: 'Passie',
    description: 'Gedreven door nieuwsgierigheid en perfectie',
    color: 'from-rose-500 to-pink-500',
    rotation: 2
  }];
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} max-w-6xl mx-auto`} data-id="element-10">
      {/* Hero Section with Pin Style */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-8 relative" data-id="element-11">
        <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-8 md:p-10`} style={{
        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
        transform: 'rotate(-0.5deg)'
      }} data-id="element-12">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" data-id="element-13" />
          <div className="absolute inset-0 opacity-5" data-id="element-14">
            <GeometricPattern pattern="circles" size="large" data-id="element-15" />
          </div>
          <div className="relative z-10" data-id="element-16">
            <div className="flex items-center gap-4 mb-6" data-id="element-17">
              <motion.div animate={{
              rotate: [0, 5, 0],
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center relative overflow-hidden" style={{
              boxShadow: '0 12px 24px -8px rgba(139, 92, 246, 0.5)'
            }} data-id="element-18">
                <GeometricPattern pattern="circles" size="medium" className="opacity-30" data-id="element-19" />
                <SparklesIcon className="w-10 h-10 text-white relative z-10" data-id="element-20" />
              </motion.div>
              <div data-id="element-21">
                <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-22">
                  {t.about?.name || 'Mick van Latum'}
                </h1>
                <p className="text-lg opacity-80" data-id="element-23">
                  {t.about?.title || 'UX Designer & AI Developer'}
                </p>
              </div>
            </div>
            <p className={`${typography.body} opacity-80 leading-relaxed max-w-3xl`} data-id="element-24">
              {t.about?.intro || 'Ik vertaal complexe vragen naar elegante oplossingen. Met een achtergrond in UX design en een passie voor AI, help ik bedrijven om gebruiksvriendelijke en intelligente digitale ervaringen te creëren.'}
            </p>
          </div>
          {/* Pin effect */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-25" />
        </div>
      </motion.div>
      {/* Core Values - Pinboard Style */}
      <div className="mb-8" data-id="element-26">
        <h2 className={`${typography.h3} mb-6 flex items-center gap-3`} data-id="element-27">
          <div className="w-1.5 h-8 rounded-full bg-gradient-to-b from-purple-500 to-pink-500" data-id="element-28" />
          {t.about?.coreValues || 'Kernwaarden'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-29">
          {coreValues.map((value, index) => {
          const Icon = value.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1 + index * 0.1
          }} onHoverStart={() => setHoveredValue(index)} onHoverEnd={() => setHoveredValue(null)} className="relative" data-id="element-30">
                <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl p-6 transition-all cursor-pointer`} style={{
              boxShadow: hoveredValue === index ? '0 20px 40px -10px rgba(0, 0, 0, 0.3)' : '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
              transform: hoveredValue === index ? 'rotate(0deg) scale(1.02)' : `rotate(${value.rotation}deg)`
            }} data-id="element-31">
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-10`} data-id="element-32" />
                  <div className="relative z-10" data-id="element-33">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-4 relative overflow-hidden`} style={{
                  boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.3)'
                }} data-id="element-34">
                      <GeometricPattern pattern="circles" size="small" className="opacity-30" data-id="element-35" />
                      <Icon className="w-7 h-7 text-white relative z-10" data-id="element-36" />
                    </div>
                    <h3 className="text-xl font-bold mb-2" data-id="element-37">{value.title}</h3>
                    <p className="opacity-80 text-sm leading-relaxed" data-id="element-38">
                      {value.description}
                    </p>
                  </div>
                </div>
                {/* Pin effect */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-39" />
              </motion.div>;
        })}
        </div>
      </div>
      {/* Background Story - Styled Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} className="relative" data-id="element-40">
        <div className={`relative rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
        boxShadow: '0 15px 35px -10px rgba(0, 0, 0, 0.2)',
        transform: 'rotate(0.5deg)'
      }} data-id="element-41">
          {/* Gradient header */}
          <div className="relative p-6 md:p-8 bg-gradient-to-br from-blue-500 to-indigo-500 text-white" data-id="element-42">
            <div className="absolute inset-0 opacity-20" data-id="element-43">
              <GeometricPattern pattern="diamond" size="large" data-id="element-44" />
            </div>
            <div className="relative z-10 flex items-center gap-4" data-id="element-45">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-46">
                <BrainIcon className="w-6 h-6 text-white" data-id="element-47" />
              </div>
              <h2 className="text-2xl font-bold" data-id="element-48">Mijn Verhaal</h2>
            </div>
          </div>
          {/* Content */}
          <div className="p-6 md:p-8 space-y-4" data-id="element-49">
            <p className={`${typography.body} opacity-80 leading-relaxed`} data-id="element-50">
              {t.about?.background || 'Mijn reis begon met een fascinatie voor hoe mensen technologie gebruiken. Door de jaren heen heb ik geleerd dat de beste oplossingen ontstaan wanneer design, technologie en menselijk begrip samenkomen.'}
            </p>
            <p className={`${typography.body} opacity-80 leading-relaxed`} data-id="element-51">
              {t.about?.passion || 'Vandaag de dag combineer ik UX design met AI om oplossingen te creëren die niet alleen mooi zijn, maar ook intelligent en gebruiksvriendelijk.'}
            </p>
          </div>
        </div>
        {/* Pin effect */}
        <div className="absolute -top-3 right-8 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-52" />
      </motion.div>
    </div>;
}